import { useMemo } from "react";
import { fmt2 } from "@/lib/money";

const money = (v) => `$${fmt2(v || 0)}`;

/**
 * SalesHistoryTable — reusable read-only sales log.
 *
 * Consumed by ItemDetail, CollectionDetail, and Portfolio Analytics.
 * Sorted newest first (backend already returns in that order but we
 * re-sort defensively so pending / optimistic rows land correctly).
 *
 * Props:
 *   sales — array of Sale rows from GET /api/sales
 *   showItemColumn — hide the Item column on ItemDetail (default true)
 *   testid — table wrapper testid
 */
export default function SalesHistoryTable({ sales = [], showItemColumn = true, testid = "sales-history-table" }) {
  const rows = useMemo(() => {
    return [...sales].sort((a, b) => {
      // Prefer sale_date (user-provided); fall back to recorded_at
      const ad = a?.sale_date || a?.recorded_at || "";
      const bd = b?.sale_date || b?.recorded_at || "";
      if (ad === bd) return 0;
      return ad > bd ? -1 : 1;
    });
  }, [sales]);

  if (rows.length === 0) {
    return (
      <div className="vp-card p-6 text-center vp-muted text-sm" data-testid={`${testid}-empty`}>
        No sales recorded yet.
      </div>
    );
  }

  return (
    <div className="vp-card overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider vp-muted border-b border-white/10">
          <tr>
            {showItemColumn && <th className="text-left px-4 py-3">Item</th>}
            <th className="text-right px-4 py-3">Qty sold</th>
            <th className="text-right px-4 py-3">Cost (ex GST)</th>
            <th className="text-right px-4 py-3">Sale price (ex GST)</th>
            <th className="text-right px-4 py-3">Realised profit (ex GST)</th>
            <th className="text-left px-4 py-3">Sale date</th>
            {showItemColumn && <th className="text-left px-4 py-3">Buyer</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const qty = Number(s.quantity_sold || 0);
            const costEx = Number(s.cost_ex_gst || s.cost_price_ex_gst || 0);
            const saleEx = Number(s.sale_price_ex_gst || s.sold_price_ex_gst || 0);
            const realised = Number(s.realised_profit_ex_gst || 0);
            const positive = realised >= 0;
            return (
              <tr key={s.id} className="border-b border-white/5 last:border-none" data-testid={`sale-row-${s.id}`}>
                {showItemColumn && (
                  <td className="px-4 py-3 truncate max-w-[240px]" title={s.item_name}>
                    {s.item_name || "(unknown)"}
                  </td>
                )}
                <td className="px-4 py-3 text-right font-mono">{qty % 1 === 0 ? qty : qty.toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-mono">{money(costEx * qty)}</td>
                <td className="px-4 py-3 text-right font-mono">{money(saleEx * qty)}</td>
                <td className={`px-4 py-3 text-right font-mono ${positive ? "text-green-500" : "text-red-500"}`}>
                  {positive ? "+" : ""}
                  {money(realised)}
                </td>
                <td className="px-4 py-3">{s.sale_date || s.sold_date || "—"}</td>
                {showItemColumn && (
                  <td className="px-4 py-3 vp-muted truncate max-w-[180px]" title={s.buyer}>
                    {s.buyer || "—"}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
